'use client';

import { useState } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Button } from '../../../components/button';
import { MetaDefault, MonoSmall } from '../../../components/typography';
import { HttpEditor } from '../../http-editor';
import { formatBytes } from '../../../utils/formatBytes';
import { renderHttpMethodText } from '../../../utils/http-badge.utils';
import type { ProbeHttpExchange } from './probe-trace-http.utils';

type UnverifiedAssetProbeHttpExchangeProps = {
  exchange: ProbeHttpExchange;
  embedded?: boolean;
};

export function UnverifiedAssetProbeHttpExchange({
  exchange,
  embedded,
}: UnverifiedAssetProbeHttpExchangeProps) {
  const t = useTranslations('designSystem.chat');
  const tAssets = useTranslations('designSystem.applicationAssets');
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Box
      borderWidth={embedded ? '0' : '1px'}
      borderColor="border.neutral.muted"
      borderRadius={embedded ? '0' : 'md'}
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="transparent"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <MetaDefault>{t('httpRequestsCount', { count: 1 })}</MetaDefault>
      </HStack>

      <VStack gap={0} alignItems="stretch" width="100%">
        <Button
          variant="ghost"
          size="sm"
          width="full"
          h="36px"
          px={3}
          justifyContent="space-between"
          borderRadius="0"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((previous) => !previous)}
        >
          <HStack gap={1.5} flex="1" minW={0}>
            <Box
              display="flex"
              alignItems="center"
              color="fg.muted"
              flexShrink={0}
              aria-hidden
            >
              {isExpanded ? (
                <ArrowDown01Icon size={10} />
              ) : (
                <ArrowRight01Icon size={10} />
              )}
            </Box>
            {renderHttpMethodText(exchange.method)}
            <MonoSmall truncate maxW="300px" title={exchange.url}>
              {exchange.path}
            </MonoSmall>
            {exchange.statusCode !== undefined
              ? renderHttpMethodText(exchange.statusCode)
              : null}
          </HStack>

          <HStack gap={1.5} flexShrink={0}>
            <MetaDefault color="fg.muted">
              {formatBytes(exchange.responseSizeBytes)}
            </MetaDefault>
          </HStack>
        </Button>

        {isExpanded ? (
          <VStack alignItems="stretch" gap="0" width="100%" h="400px">
            <HStack
              alignItems="stretch"
              w="full"
              bg="bg.subtle"
              borderBottomWidth="1px"
              borderColor="border.muted"
              flexShrink={0}
            >
              <Box
                flex="1"
                px="2"
                py="1"
                minH="8"
                display="flex"
                alignItems="center"
              >
                <MetaDefault fontWeight="medium">
                  {tAssets('probeTraceRequest')}
                </MetaDefault>
              </Box>
              <Box w="1px" bg="border.subtle" alignSelf="stretch" />
              <Box
                flex="1"
                px="2"
                py="1"
                minH="8"
                display="flex"
                alignItems="center"
              >
                <HStack gap="2">
                  <MetaDefault fontWeight="medium">
                    {tAssets('probeTraceResponse')}
                  </MetaDefault>
                  {exchange.statusCode !== undefined ? (
                    <MetaDefault color="fg.muted">
                      {exchange.statusCode}
                    </MetaDefault>
                  ) : null}
                </HStack>
              </Box>
            </HStack>
            <HStack alignItems="stretch" gap="0" width="100%" flex="1" minH={0}>
              <Box flex="1" minH={0} minW={0} h="full">
                <HttpEditor
                  value={exchange.requestHttp}
                  readOnly
                  showViewModeToggle
                  canonicalRequestUrl={exchange.url}
                />
              </Box>
              <Box w="1px" bg="border.subtle" flexShrink={0} />
              <Box flex="1" minH={0} minW={0} h="full">
                <HttpEditor
                  value={exchange.responseHttp}
                  readOnly
                  showViewModeToggle
                />
              </Box>
            </HStack>
          </VStack>
        ) : null}
      </VStack>
    </Box>
  );
}
